<template>
  <div
    class="ExperienceRangeInput"
    @pointerdown="handlePointerInteract"
  >
    <div
      class="ExperienceRangeInput__header"
      data-testid="header"
      aria-hidden="true"
    >
      <Transition>
        <span
          :key="modelValue === null ? 0 : 1"
          class="ExperienceRangeInput__headerTitle"
        >{{ formattedHeader }}</span>
      </Transition>
    </div>

    <div class="ExperienceRangeInput__trackContainer">
      <div
        class="ExperienceRangeInput__track"
      >
        <div
          v-for="(item, i) in items"
          :key="i"
          class="ExperienceRangeInput__trackTick"
          :data-size="!!item.tick ? 'large' : 'small'"
          :style="{
            left: `${i / (items.length - 1) * 100}%`,
          }"
        />
      </div>

      <Basketball
        v-show="hasValue"
        class="ExperienceRangeInput__thumb"
        data-testid="thumb"
      />
    </div>

    <div class="ExperienceRangeInput__tickLabelContainer">
      <div
        v-for="tick in ticks"
        :key="tick.value"
        class="ExperienceRangeInput__tickLabel"
        data-testid="tickLabel"
        :style="{
          left: `${tick.value / valueMax * 100}%`,
        }"
      >
        {{ tick.tick }}
      </div>
    </div>

    <input
      v-model.number="modelValue"
      v-bind="{step}"
      class="ExperienceRangeInput__input"
      type="range"
      :min="valueMin"
      :max="valueMax"
      :aria-valuetext="formattedHeader"
    >

    <button
      v-show="hasValue"
      class="ExperienceRangeInput__resetButton"
      type="button"
      aria-label="Reset experience"
      data-testid="resetButton"
      @click="handleResetButtonClick"
    >
      <Icon
        name="circleX"
        size="large"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import haptic from "@/lib/utils/platform/haptic";
import { computed, ref, watch } from "vue";
import Icon from "./Icon.vue";

const modelValue = defineModel<number | null>({
  required: true,
});

const valueMin = 0;
const valueMax = 90;
const step = 10;

const items = ref([
  {
    label: "Casual",
    tick: "Rec",
    value: 0,
  },
  {
    label: "Pickup",
    value: 10,
  },
  {
    label: "Varsity",
    tick: "HS",
    value: 20,
  },
  {
    label: "AAU / Travel",
    value: 30,
  },
  {
    label: "JUCO / NAIA",
    tick: "College",
    value: 40,
  },
  {
    label: "D3",
    value: 50,
  },
  {
    label: "D2",
    value: 60,
  },
  {
    label: "D1",
    value: 70,
  },
  {
    label: "G-League / Overseas",
    value: 80,
  },
  {
    label: "NBA / WNBA / Euro",
    tick: "Pro",
    value: 90,
  },
]);

watch(modelValue, () => {
  haptic();
});

const hasValue = computed(() => {
  return modelValue.value !== null;
});

const ticks = computed(() => {
  return items.value.filter((item) => !!item.tick);
});

const formattedHeader = computed(() => {
  return items.value.find((item) => item.value === modelValue.value)?.label ?? "Tap to set";
});

const thumbPosition = computed(() => {
  if (modelValue.value === null) return valueMax - valueMin;

  return modelValue.value - valueMin;
});

function handleResetButtonClick () {
  modelValue.value = null;
}

function handlePointerInteract (e: PointerEvent) {
  if (hasValue.value) return;

  const padding = 16;
  const ratio = (e.offsetX - padding) / ((e.target as HTMLElement).clientWidth - padding * 2);
  modelValue.value = Math.max(valueMin, Math.min(valueMax, Math.round((valueMax - valueMin) * ratio / step) * step));
}
</script>

<style lang="scss" scoped>
.ExperienceRangeInput {
  --thumbRadius: 16px;
  --thumbSize: calc(2 * var(--thumbRadius));
  --inputThumbSize: calc(2 * (16px + var(--thumbRadius)));
  --inputHeight: 122px;
  position: relative;

  display: grid;
  padding: 20px 16px 16px;

  border: 2px solid var(--themeColorNeutral900);
  border-radius: 6px;
  background-color: var(--themeColorInputBackground);
  color: var(--themeColorInputForeground);
  box-shadow: 0 0 0 0 transparent;

  transition: 350ms;
  transition-property: box-shadow, border-color, background-color;

  &:has(:disabled) {
    border-color: var(--themeColorNeutral400);
    background-color: var(--themeColorNeutral0);
    color: var(--themeDarkTransparent500);
  }

  &:focus-within {
    box-shadow: 0 0 0 3px var(--themeColorSecondary);
  }
}

.ExperienceRangeInput__header {
  position: relative;

  display: grid;
  place-items: center;
  margin-bottom: 20px;

  font: var(--fontWeightNormal) 18px/1 var(--fontFamilyMono);
}

.ExperienceRangeInput__headerTitle {
  display: block;
  transition: opacity 150ms;

  &.v-leave-active {
    position: absolute;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.ExperienceRangeInput__tickLabelContainer {
  position: relative;
  height: 1lh;
  margin: 8px var(--thumbRadius) 0;
}

.ExperienceRangeInput__tickLabel {
  position: absolute;
  top: 0;
  transform: translateX(-50%);

  font: var(--fontWeightNormal) 16px/1 var(--fontFamilyMono);
}

.ExperienceRangeInput__input {
  position: absolute;
  inset: 0;

  outline: none;
  background: none;
  appearance: none;

  &::-webkit-slider-runnable-track {
    display: grid;
    align-items: center;
    height: var(--inputHeight);
  }

  &::-moz-range-track {
    display: grid;
    align-items: center;
    height: var(--inputHeight);
  }

  &::-webkit-slider-thumb {
    width: var(--inputThumbSize);
    height: var(--inputHeight);

    background-color: transparent;
    box-shadow: none;
    appearance: none;
  }

  &::-moz-range-thumb {
    width: var(--inputThumbSize);
    height: var(--inputHeight);

    border: none;
    border-radius: 0;
    background-color: transparent;
  }

  &:not(:disabled) {
    cursor: pointer;
  }
}

.ExperienceRangeInput__resetButton {
  --buttonSize: 44px;
  --iconSize: 20px;
  position: absolute;
  top: calc(8px - (var(--buttonSize) - var(--iconSize)) / 2);
  right: calc(8px - (var(--buttonSize) - var(--iconSize)) / 2);

  display: grid;
  place-items: center;
  width: var(--buttonSize);
  height: var(--buttonSize);

  color: var(--themeColorNeutral300);
}

.ExperienceRangeInput__trackContainer {
  position: relative;

  display: grid;
  align-items: center;
  height: 24px;

  pointer-events: none;
}

.ExperienceRangeInput__track {
  position: relative;

  height: 6px;
  margin: 0 var(--thumbRadius);

  background-color: var(--themeColorNeutral150);
}

.ExperienceRangeInput__trackTick {
  position: absolute;
  top: 50%;
  background-color: inherit;

  &[data-size="small"] {
    transform: translate(-1px, -50%);
    width: 2px;
    height: 16px;
  }

  &[data-size="large"] {
    transform: translate(-2px, -50%);
    width: 3px;
    height: 24px;
  }
}

.ExperienceRangeInput__thumb {
  position: absolute;
  top: calc(50% - var(--thumbRadius));
  left: calc((100% - var(--thumbSize)) * v-bind(thumbPosition) / v-bind(valueMax));

  .ExperienceRangeInput:has(:disabled) & {
    --iconColor: var(--themeColorNeutral150);
    --strokeColor: var(--themeColorNeutral500);
  }
}
</style>
